// Making a Schedule of Work from what's on the device: the measure's areas,
// their photos (loaded for the PDF), the options chosen — then the file,
// downloaded, and saved to the job site + listed in CRM when there's signal.
import type { AccessTag, JobPrice, SurfaceType, TickRow } from '@higgins/m2q-pricing';
import { getPricingReference, isConnectionFailure, resolvePaints, saveDocumentToApi } from '../api';
import { isoToday } from '../dates';
import { productIdsToPrice } from '../use-paints';
import type { PhotoSources } from '../area-photos';
import type { DeviceJob, DevicePhoto, DeviceRecord } from '../device-db';
import { buildingCountOf, scheduleAreas, scheduleFileName, type PhotoRef, type ScheduleOptions } from './model';
import type { LineNames } from './priced-names';
import { NO_REFERENCE, readScheduleReference, type PaintsFor, type ScheduleReference } from './pricing-reference';
import type { ScheduleSystems } from './systems';
import type { CoverDetails } from './pdf-cover';
import { drawSchedule } from './pdf-document';
import { areaPhotos, loadPhotos } from './photos';
import { asTickRow, tickRowsOf } from '../tick-rows';

/** An area with its tick rows, for its access and preliminaries, and its building (#526). */
export type MeasureArea = DeviceRecord & { tickRows: TickRow[]; building: { name: string; count: number } };

/** The measure's areas, building by building, in each building's order, each with its building's name and count (#526). */
export function measureAreas(records: readonly DeviceRecord[], measureId: string): MeasureArea[] {
  const order = (r: DeviceRecord) => Number(r.fields.hig_sortorder ?? 0);
  const buildings = records.filter((r) => r.table === 'building' && r.parentId === measureId).sort((a, b) => order(a) - order(b));
  return buildings.flatMap((b) => {
    const building = { name: String(b.fields.hig_name ?? ''), count: buildingCountOf(b.fields.hig_count) };
    return records
      .filter((r) => r.table === 'area' && r.parentId === b.id)
      .sort((a, c) => order(a) - order(c))
      .map((a) => ({ ...a, building, tickRows: tickRowsOf(records, 'areatag', a.id).map(asTickRow) }));
  });
}

export const allPhotos = (areas: readonly DeviceRecord[], sources: PhotoSources): PhotoRef[] => areas.flatMap((a) => areaPhotos(a, sources));

/** The wash rate (#510) and the hire list (#464) on the version's pricing date, from one read (#530); nothing without signal. */
/** What the reference read needs to rate the Schedule option's paints as pricing does (litres as priced). */
export const paintsFor = (systems: ScheduleSystems, jobPrices: readonly JobPrice[]): Omit<PaintsFor, 'resolve'> => ({ scope: systems.scope, jobPrices, productIds: productIdsToPrice(systems.scope) });

export async function scheduleReference(jobId: string, version: DeviceRecord | null, paintsFor: Omit<PaintsFor, 'resolve'> | null): Promise<ScheduleReference> {
  if (!navigator.onLine) return NO_REFERENCE;
  return readScheduleReference((date) => getPricingReference(jobId, date), version, isoToday(), paintsFor && { ...paintsFor, resolve: resolvePaints });
}

export interface GenerateInput {
  job: DeviceJob;
  areas: MeasureArea[];
  version: DeviceRecord | null;
  options: ScheduleOptions;
  /** The option whose paint systems and priced lines it shows (systems.ts). */
  systems: ScheduleSystems | null;
  /** The wash.rate setting (scheduleReference). */
  washRate: number | null;
  /** What the option's preliminary and hire lines are named from (priced-names.ts, #464). */
  names: LineNames;
  cover: Omit<CoverDetails, 'jobNumber' | 'jobName' | 'client' | 'site' | 'estimator' | 'paintSystemsFrom'>;
  types: ReadonlyMap<string, SurfaceType>;
  tags: readonly AccessTag[];
  /** Every photo of the measure's areas (allPhotos). */
  photos: PhotoRef[];
  onDevice: DevicePhoto[];
  /** YYYY-MM-DD, for the file name. */
  isoDate: string;
}

export interface Generated {
  fileName: string;
  blob: Blob;
}

export async function generateSchedule(input: GenerateInput, progress: (message: string) => void): Promise<Generated> {
  const { job, areas, version, options, systems, names, washRate, types, tags, photos, onDevice } = input;
  const built = scheduleAreas(areas, types, tags, photos, options, systems, names, washRate);
  const wanted = photos.filter((p) => p.id === options.coverPhotoId || built.some((a) => a.photos.some((x) => x.id === p.id)));
  progress(wanted.length ? `Loading photos (0 of ${wanted.length})…` : 'Building the document…');
  const loaded = await loadPhotos(wanted, onDevice, (done, total) => progress(`Loading photos (${done} of ${total})…`));
  progress('Building the document…');
  const d = job.details;
  const blob = await drawSchedule({
    cover: { ...input.cover, jobNumber: job.number, jobName: d?.jobName ?? null, client: d?.client ?? null, site: d?.site ?? null, estimator: d?.estimator ?? null, paintSystemsFrom: built.some((a) => a.hasSystems) ? (systems?.optionName ?? null) : null },
    areas: built,
    options,
    photos: loaded,
  });
  const versionNumber = typeof version?.fields.hig_versionnumber === 'number' ? version.fields.hig_versionnumber : null;
  return { fileName: scheduleFileName(job.number, versionNumber, input.isoDate), blob };
}

/** Save to the job site and list it in CRM. Explains rather than throws when it can't. */
export async function saveToJob(generated: Generated, version: DeviceRecord | null): Promise<{ webUrl: string } | { problem: string }> {
  if (!navigator.onLine) return { problem: 'No signal — not saved to the job. Make it again with signal to save it.' };
  if (!version || version.etag === null) return { problem: "The scope version hasn't reached CRM yet — not saved to the job. Sync, then make it again to save it." };
  try {
    return await saveDocumentToApi(version.id, generated.fileName, generated.blob);
  } catch (err) {
    if (isConnectionFailure(err)) return { problem: 'Lost signal while saving — not saved to the job.' };
    return { problem: `Not saved to the job: ${err instanceof Error ? err.message : String(err)}` };
  }
}
